import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { commonClassName, commonHeaderClass } from './PromoScorecard';
import BarPointChartContainer, {
    chartHeight,
    priceLadderContainerId,
    yAxisWidth,
} from 'components/template/PriceLadder/BarPointChartContainer';
import { getAdditionalDataOnCopyClick, getConfig, parseAttributeType } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import TileButton from 'components/atoms/TileButton';
import BarChartContainer, { barChartContainerId } from 'components/template/PriceLadder/BarChartContainer';
import { CustomTable } from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import { getPriceLadderTableColumns } from 'utils/getPriceLadderTableColumns';
import { salesGraphWidth } from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { FilterContextData } from 'contexts/FilterContext';
import { useFetchPriceLadder } from 'hooks/priceLadder/useFetchPriceLadder';
import _ from 'lodash';
import { useFetchPriceLadderChart2 } from 'hooks/priceLadder/useFetchPriceLadderChart2';
import { useFetchCompetitorPriceLadder } from 'hooks/priceLadder/useFetchCompetitorPriceLadder';
import { useFetchCompetitorPriceLadderChart2 } from 'hooks/priceLadder/useFetchCompetitorChart2';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { priceLadderTableDownload } from 'parser/graphToExcelDownloadParser';
import GroupedLineChartContainer from 'components/template/PriceLadder/GroupedLineChartContainer';
import { useFetchMultiLineData } from 'hooks/priceLadder/useFetchMultilineData';
import { ConfigKeys } from 'utils/Enums';
const priceLadderMainHeader = 'Price Ladder';

const priceLadderTableId = 'price-ladder-table-wrapper';

const PriceLadder = () => {
    const [attributeTypesList, setAttributeTypesList] = useState<any[]>([]);
    const [selectPPG, setSelectPPG] = useState<any>(null);

    const { appConfig } = useContext(UserContext);
    const priceLadderConfig = getConfig('priceLadder', appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const [applyFilterTempLoading, setApplyFilterTempLoading] = useState(false);
    const [tileData, setTileData] = useState(priceLadderConfig.tileData[0]);
    const [dropdownFilterIds, setDropdownFilterIds] = useState<any>({});
    const [subTileState, setSubTileState] = useState(priceLadderConfig.subTileData[0]);
    const [selectedKpi, setSelectedKpi] = useState<any>();
    const [selectedAttributeItem, setSelectedAttributeItem] = useState<string>('');
    const [thresholdValue, setThresholdValue] = useState('');
    const [dummyThresholdRender, setDummyThresholdRender] = useState(0);
    const offsetRef = useRef(0);
    const tableRef = useRef<any>();
    const {
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        timePeriodLoading,
        timePeriodSelection,
        idForRestOfComponentLoading,
        allCheckedAttribute,
        timeSelectionLabel,
        filterNullError,
    } = useContext(FilterContextData);

    const {
        data: priceLadderData,
        error: priceLadderError,
        loading: priceLadderLoading,
        count: priceLadderCount,
        fetchPriceLadder,
    } = useFetchPriceLadder({ setSelectedAttributeItem, subTileState });

    const {
        data: priceLadderCompetitorData,
        error: priceLadderCompetitorDataError,
        loading: priceLadderCompetitorDataLoading,
        fetchCompetitorPriceLadder: fetchPriceLadderCompetitor,
        setError: setCompetitorPriceLadderError,
    } = useFetchCompetitorPriceLadder();

    const {
        data: priceLadderChart2Data,
        error: priceLadderChart2Error,
        loading: priceLadderChart2Loading,
        count: priceLadderChart2Count,
        fetchPriceLadderChart2,
    } = useFetchPriceLadderChart2();

    const {
        data: multiLineData,
        error: multiLineError,
        loading: multiLineLoading,
        fetchMultiLineChartData,
    } = useFetchMultiLineData();

    const {
        data: competitorPriceLadderChart2Data,
        error: competitorPriceLadderChart2Error,
        loading: competitorPriceLadderChart2Loading,
        fetchCompetitorPriceLadderChart2,
        setError: setCompetitorPriceLadderChart2Error,
    } = useFetchCompetitorPriceLadderChart2();

    //graph main axis width
    const containerWidth = useContainerWidth({
        containerId: priceLadderContainerId,
        buffer: yAxisWidth * 2 + 34 * 2,
    });

    //infinite scroll width ref
    const infiniteScrollWidthRef = useRef(0);

    //graph and table width
    const graphViewContainerWidth =
        Math.max(
            tileData.key === priceLadderConfig.tileData?.[0]?.key && priceLadderData
                ? (priceLadderData?.chartData?.data?.[subTileState.key]?.length ||
                      priceLadderChart2Data?.chartData?.data?.length) * salesGraphWidth
                : tileData.key === priceLadderConfig?.tileData?.[1]?.key && priceLadderCompetitorData
                ? (priceLadderCompetitorData?.chartData?.data?.[subTileState.key]?.length ||
                      competitorPriceLadderChart2Data?.chartData?.data?.length) * salesGraphWidth
                : 0,
            containerWidth,
        ) || window.innerWidth;
    const perGraphComponentWidth =
        graphViewContainerWidth /
            (tileData.key === priceLadderConfig.tileData[0].key
                ? priceLadderData?.chartData?.data?.[subTileState.key]?.length ||
                  priceLadderChart2Data?.chartData?.data?.length
                : priceLadderCompetitorData?.chartData?.data?.[subTileState.key]?.length ||
                  competitorPriceLadderChart2Data?.chartData?.data?.length) || 0;

    //graph width config
    const widthConfig = useMemo(() => {
        return {
            containerWidth,
            graphViewContainerWidth,
            perGraphComponentWidth,
        };
    }, [containerWidth, graphViewContainerWidth, perGraphComponentWidth]);

    const memoisedColumns = useMemo(() => {
        return getPriceLadderTableColumns(
            tileData.key === priceLadderConfig.tileData[0].key
                ? priceLadderData?.tableData
                : priceLadderCompetitorData?.tableData,
            perGraphComponentWidth,
            decimalForNonPercentage,
            decimalForPercentage,
        );
    }, [widthConfig, priceLadderData, priceLadderCompetitorData]);

    //adding scroll event listener
    useLayoutEffect(() => {
        const tableScrollableElement = document
            .getElementById(priceLadderTableId)
            ?.getElementsByClassName('ant-table-body')[0];
        //graph element
        const graphElem = document.getElementById(priceLadderContainerId);
        const barChartElem = document.getElementById(barChartContainerId);

        //scroll table and 2nd graph when user is scrolling sales chart
        function scrollFunction(event: any) {
            tableScrollableElement?.scrollTo({
                left: Number(graphElem?.scrollLeft) * 1, //giving a buffer because widths of table and chart are different
                behavior: 'auto',
            });

            // bar chart element
            barChartElem?.scrollTo({
                left: Number(graphElem?.scrollLeft) * 1, //giving a buffer because widths of table and chart are different
                behavior: 'auto',
            });
        }
        graphElem?.addEventListener('scroll', scrollFunction, { passive: true });
        return () => {
            graphElem?.removeEventListener('scroll', scrollFunction);
        };
    }, [priceLadderData, priceLadderChart2Data, competitorPriceLadderChart2Data, priceLadderCompetitorData]);

    //price ladder chart 1
    useEffect(() => {
        //checking if global filter data is loaded to global filter context
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading &&
            selectPPG
        ) {
            //resetting offset and infinite scroller
            offsetRef.current = 0;
            infiniteScrollWidthRef.current = 0;
            getPriceLadderData([1], offsetRef.current);
        }
    }, [
        idForRestOfComponent,
        selectPPG,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        timePeriodSelection,
    ]);

    //price ladder chart 2
    useEffect(() => {
        //checking if global filter data is loaded to global filter context
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading &&
            selectPPG &&
            (priceLadderConfig.tileData?.length === 1 ? selectedAttributeItem : true)
        ) {
            //resetting offset and infinite scroller
            offsetRef.current = 0;
            infiniteScrollWidthRef.current = 0;
            getPriceLadderData([2], offsetRef.current);
        }
    }, [
        idForRestOfComponent,
        selectPPG,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        timePeriodSelection,
    ]);

    //price ladder chart 2 for selection item
    useEffect(() => {
        //checking if global filter data is loaded to global filter context
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading &&
            selectedKpi &&
            (priceLadderConfig.tileData?.length === 1 ? selectedAttributeItem : true)
        ) {
            //resetting offset and infinite scroller
            //offsetRef.current = 0;
            infiniteScrollWidthRef.current = 0;
            getPriceLadderData([2], offsetRef.current, true);
        }
    }, [
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        timePeriodSelection,
        selectedAttributeItem,
        selectedKpi,
    ]);

    //competitor price ladder data
    useEffect(() => {
        if (dropdownFilterIds && 'dim1' in dropdownFilterIds) {
            //resetting offset and infinite scroller
            offsetRef.current = 0;
            infiniteScrollWidthRef.current = 0;
            fetchPriceLadderCompetitor({
                variables: {
                    dimension: selectPPG?.dimension,
                    attributeType: _.snakeCase(selectPPG?.label),
                    limit: priceLadderConfig?.limit,
                    //limit: 50,
                    globalFilters: JSON.stringify(idForRestOfComponent),
                    attributeValues: JSON.stringify(dropdownFilterIds),
                    offset: offsetRef.current,
                    timePeriod: String(timePeriodSelection).toLowerCase(),
                },
            });
        }
    }, [dropdownFilterIds, timePeriodSelection, idForRestOfComponent]);

    //competitor chart 2 data

    useEffect(() => {
        if (dropdownFilterIds && 'dim1' in dropdownFilterIds) {
            //resetting offset and infinite scroller
            offsetRef.current = 0;
            infiniteScrollWidthRef.current = 0;
            fetchCompetitorPriceLadderChart2({
                variables: {
                    metric: selectedKpi?.key,
                    metricType: selectedKpi?.metricKey,
                    dimension: selectPPG?.dimension,
                    attributeType: _.snakeCase(selectPPG?.label),
                    limit: priceLadderConfig?.limit,
                    //limit: 50,
                    filters: JSON.stringify(idForRestOfComponent),
                    attributeValues: JSON.stringify(dropdownFilterIds),

                    offset: offsetRef.current,
                    timePeriod: String(timePeriodSelection).toLowerCase(),
                },
            });
        }
    }, [dropdownFilterIds, selectedKpi, timePeriodSelection, idForRestOfComponent]);

    useEffect(() => {
        setApplyFilterTempLoading(competitorPriceLadderChart2Loading && priceLadderCompetitorDataLoading);
    }, [competitorPriceLadderChart2Loading, priceLadderCompetitorDataLoading]);

    // container function which fetches price ladder data
    const getPriceLadderData = useCallback(
        (arr: (1 | 2)[], offset: number, infiniteScrollEnabled: boolean = false) => {
            offsetRef.current = offset;
            if (tileData.key === priceLadderConfig.tileData[0].key) {
                //if arr includes 2 ,get data for chart 2 and chart 3
                if (arr.includes(2)) {
                    fetchPriceLadderChart2({
                        variables: {
                            metric: selectedKpi?.key,
                            metricType: selectedKpi?.metricKey,
                            dimension: selectPPG?.dimension,
                            attributeType: _.snakeCase(selectPPG?.label),
                            attributeName: selectedAttributeItem,
                            version: priceLadderConfig.tileData?.length === 1 ? 'v2' : 'v1',
                            limit: infiniteScrollEnabled ? offset + priceLadderConfig?.limit : priceLadderConfig?.limit,
                            filters: JSON.stringify(idForRestOfComponent),
                            offset: infiniteScrollEnabled ? 0 : offset,
                            timePeriod: timePeriodSelection?.toLowerCase(),
                        },
                    });
                    fetchMultiLineChartData({
                        variables: {
                            metric: selectedKpi?.key,
                            metricType: selectedKpi?.metricKey,
                            dimension: selectPPG?.dimension,
                            attributeType: _.snakeCase(selectPPG?.label),
                            attributeName: selectedAttributeItem,
                            version: 'v2',
                            limit: 50, //here limit does not play a role as only 10 items will be present at max
                            filters: JSON.stringify(idForRestOfComponent),
                            offset: 0,
                            timePeriod: timePeriodSelection?.toLowerCase(),
                        },
                    });
                    // fetchMultiLineChartData({ variables: dummyData });
                }
                //if arr includes 1 , get data for chart 1
                if (arr.includes(1)) {
                    fetchPriceLadder({
                        variables: {
                            dimension: selectPPG?.dimension,
                            attributeType: _.snakeCase(selectPPG?.label),
                            limit: priceLadderConfig?.limit,
                            version: priceLadderConfig.tileData?.length === 1 ? 'v2' : 'v1',
                            filters: JSON.stringify(idForRestOfComponent),
                            offset: offset,
                            timePeriod: String(timePeriodSelection).toLowerCase(),
                            salesFilter: !thresholdValue ? null : Number(thresholdValue),
                        },
                    });
                }
            }
        },
        [selectedKpi, subTileState, idForRestOfComponent, timePeriodSelection, selectPPG, selectedAttributeItem],
    );

    useEffect(() => {
        if (dummyThresholdRender > 0) {
            fetchPriceLadder({
                variables: {
                    dimension: selectPPG?.dimension,
                    attributeType: _.snakeCase(selectPPG?.label),
                    limit: priceLadderConfig?.limit,
                    version: priceLadderConfig.tileData?.length === 1 ? 'v2' : 'v1',
                    filters: JSON.stringify(idForRestOfComponent),
                    offset: offsetRef.current,
                    timePeriod: String(timePeriodSelection).toLowerCase(),
                    salesFilter: !thresholdValue ? null : Number(thresholdValue),
                },
            });
        }
    }, [dummyThresholdRender]);

    function setError(error: any) {
        setCompetitorPriceLadderError(error);
        setCompetitorPriceLadderChart2Error(error);
    }

    const commonLoading =
        priceLadderLoading ||
        priceLadderCompetitorDataLoading ||
        priceLadderChart2Loading ||
        competitorPriceLadderChart2Loading ||
        applyFilterTempLoading ||
        allDimAndAttributeTypeList?.loading;

    const tableExcelData = priceLadderTableDownload(
        tileData.key === priceLadderConfig.tileData[0].key
            ? priceLadderData?.tableData
            : priceLadderCompetitorData?.tableData,
    );

    return (
        <section className={`${commonClassName} bg-white`}>
            <div className="flex gap-4 mb-4 items-center">
                <h2 className={commonHeaderClass}>{priceLadderMainHeader}</h2>
                {priceLadderConfig.tileData?.length > 1 && (
                    <TileButton
                        disabled={commonLoading}
                        data={priceLadderConfig.tileData}
                        activeKey={tileData.key}
                        onClick={(item: any) => {
                            setTileData(item);
                            //mixpanel event
                        }}
                    />
                )}
            </div>
            <BarPointChartContainer
                count={priceLadderCount}
                setSelectedAttributeItem={setSelectedAttributeItem}
                selectedAttributeItem={selectedAttributeItem}
                setError={setError}
                setApplyFilterTempLoading={setApplyFilterTempLoading}
                applyFilterTempLoading={applyFilterTempLoading}
                infiniteScrollWidthRef={infiniteScrollWidthRef}
                offset={offsetRef.current}
                getPriceLadderData={getPriceLadderData}
                setDropdownFilterIds={setDropdownFilterIds}
                error={
                    tileData.key === priceLadderConfig.tileData[0].key
                        ? priceLadderError
                        : priceLadderCompetitorDataError
                }
                data={
                    tileData.key === priceLadderConfig.tileData[0].key
                        ? priceLadderData?.chartData
                        : priceLadderCompetitorData?.chartData
                }
                tileData={tileData}
                attributeTypesList={attributeTypesList}
                setAttributeTypesList={setAttributeTypesList}
                selectPPG={selectPPG}
                setSelectPPG={setSelectPPG}
                subTileState={subTileState}
                setSubTileState={setSubTileState}
                widthConfig={widthConfig}
                loading={priceLadderLoading || priceLadderCompetitorDataLoading}
                thresholdValue={thresholdValue}
                setThresholdValue={setThresholdValue}
                setDummyThresholdRender={setDummyThresholdRender}
            />
            <BarChartContainer
                selectedAttributeItem={selectedAttributeItem}
                infiniteScrollWidthRef={infiniteScrollWidthRef}
                offset={offsetRef.current}
                getPriceLadderData={getPriceLadderData}
                selectedKpi={selectedKpi}
                setSelectedKpi={setSelectedKpi}
                isMarket={tileData.key === priceLadderConfig.tileData[0].key}
                priceLadderChart2Error={
                    tileData.key === priceLadderConfig.tileData[0].key
                        ? priceLadderChart2Error
                        : competitorPriceLadderChart2Error
                }
                priceLadderChart2Data={
                    tileData.key === priceLadderConfig.tileData[0].key
                        ? priceLadderChart2Data
                        : competitorPriceLadderChart2Data
                }
                priceLadderChart2Loading={
                    applyFilterTempLoading ||
                    priceLadderLoading ||
                    priceLadderChart2Loading ||
                    competitorPriceLadderChart2Loading ||
                    priceLadderCompetitorDataLoading
                }
                widthConfig={widthConfig}
                selectPPG={selectPPG}
                subTileState={subTileState}
            />
            {tileData.key === priceLadderConfig.tileData[0].key && (
                <GroupedLineChartContainer
                    multiLineData={multiLineData}
                    error={multiLineError}
                    loading={
                        allDimAndAttributeTypeList?.loading ||
                        applyFilterTempLoading ||
                        priceLadderLoading ||
                        multiLineLoading
                    }
                    subTileState={subTileState}
                    selectedKpi={selectedKpi}
                    setSelectedKpi={setSelectedKpi}
                    selectedAttributeItem={selectedAttributeItem}
                    selectPPG={selectPPG}
                />
            )}

            <div id={priceLadderTableId} className="mt-4">
                {!(
                    applyFilterTempLoading ||
                    priceLadderLoading ||
                    allDimAndAttributeTypeList?.loading ||
                    priceLadderCompetitorDataLoading
                ) && (
                    <div className="flex justify-end gap-2 mb-2">
                        <CopyToClipBoard refOfComponent={tableRef} />

                        <DownloadExcel
                            title={'PriceLadderTable'}
                            excelData={tableExcelData.data}
                            headers={tableExcelData.headers}
                        />
                    </div>
                )}
                <CustomTable
                    ref={tableRef}
                    bordered={false}
                    pagination={false}
                    scroll={{ x: '100%', y: chartHeight + 200 }}
                    sticky
                    // style={style}
                    // rowKey={(record: any) => record[rowKey]}
                    columns={memoisedColumns}
                    dataSource={
                        tileData.key === priceLadderConfig.tileData[0].key
                            ? priceLadderData?.tableData
                            : priceLadderCompetitorData?.tableData
                    }
                    loading={
                        applyFilterTempLoading ||
                        priceLadderLoading ||
                        allDimAndAttributeTypeList?.loading ||
                        priceLadderCompetitorDataLoading
                    }
                />
            </div>
        </section>
    );
};

export default PriceLadder;
